<script setup lang="ts">
/**
 * Section 259(2) — Notice of Extension of Time — M32015
 * · company_type 에 따라 Private/Public 섹션 채움 (상대는 NIL)
 * · DCR 과 company_type 공유
 */
import { computed, inject } from 'vue';
import { EXTENSION_OF_TIME_DOC_CTX } from './extensionOfTimeContext';
import ExtensionOfTimeDocVarField from './ExtensionOfTimeDocVarField.vue';
import ExtensionSigners from './parts/ExtensionSigners.vue';

const ctx = inject(EXTENSION_OF_TIME_DOC_CTX)!;
const companyType = computed(() => ctx.choiceOf('company_type') || 'private');
const isPrivate = computed(() => companyType.value === 'private');

function setType(t: 'private' | 'public') {
  ctx.setChoice('company_type', t);
}
</script>

<template>
  <div class="eot-doc">
    <div class="doc-head">
      <div class="doc-meta">COMPANIES ACT 2016</div>
      <div class="doc-meta">Section 259(2)</div>
      <div class="doc-company" style="font-size:13px; margin-top:6px">
        NOTICE OF EXTENSION OF TIME TO LODGE FINANCIAL STATEMENTS AND REPORTS
      </div>
      <div class="doc-company" style="margin-top:8px">
        <ExtensionOfTimeDocVarField field-key="company_name" />
      </div>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr><th colspan="2">DETAILS OF COMPANY</th></tr>
        <tr>
          <td class="cdd-lcell">Name</td>
          <td><ExtensionOfTimeDocVarField field-key="company_name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Address of registered office</td>
          <td><ExtensionOfTimeDocVarField field-key="registered_office_address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Company No</td>
          <td><ExtensionOfTimeDocVarField field-key="company_registration_no" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Tel No</td>
          <td><ExtensionOfTimeDocVarField field-key="company_phone" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Fax No</td>
          <td><ExtensionOfTimeDocVarField field-key="company_fax" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">E-mail</td>
          <td><ExtensionOfTimeDocVarField field-key="company_email" /></td>
        </tr>
      </tbody>
    </table>

    <p class="note-line">
      Note: Complete the section applicable to your company type; the other section is marked NIL automatically.
    </p>

    <p class="type-row">
      <span
        class="doc-toggle type-chip"
        :class="{ on: isPrivate }"
        @click="setType('private')"
      >{{ isPrivate ? '☑' : '☐' }} Private Company</span>
      <span
        class="doc-toggle type-chip"
        :class="{ on: !isPrivate }"
        @click="setType('public')"
      >{{ !isPrivate ? '☑' : '☐' }} Public Company</span>
    </p>

    <table class="cdd-tbl" style="margin-top:10px">
      <tbody>
        <tr>
          <th colspan="2">
            APPLICATION FOR EXTENSION OF TIME — PRIVATE COMPANY
            <span v-if="isPrivate" class="sec-badge">Active</span>
          </th>
        </tr>
        <template v-if="isPrivate">
          <tr>
            <td class="cdd-lcell" style="width:58%">Date of Financial Year End</td>
            <td><ExtensionOfTimeDocVarField field-key="financial_year_end" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Last date for the Circulation of Financial Statement (within 6 months from date of Financial Year End)</td>
            <td><ExtensionOfTimeDocVarField field-key="circulation_deadline" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Proposed period for extension of the Circulation of Financial Statement (Fee: RM 100)</td>
            <td><ExtensionOfTimeDocVarField field-key="proposed_circulation_extension" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Proposed Period for extension of the Financial Statement Lodgement (Fee: RM 100)</td>
            <td><ExtensionOfTimeDocVarField field-key="proposed_lodgement_extension" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Reasons for applying the extension of time</td>
            <td><ExtensionOfTimeDocVarField field-key="extension_reason" /></td>
          </tr>
        </template>
        <template v-else>
          <tr><td class="cdd-lcell" style="width:58%">Date of Financial Year End</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Last date for the Circulation of Financial Statement (within 6 months from date of Financial Year End)</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Proposed period for extension of the Circulation of Financial Statement (Fee: RM 100)</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Proposed Period for extension of the Financial Statement Lodgement (Fee: RM 100)</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Reasons for applying the extension of time</td><td class="nil">NIL</td></tr>
        </template>
      </tbody>
    </table>

    <table class="cdd-tbl" style="margin-top:14px">
      <tbody>
        <tr>
          <th colspan="2">
            APPLICATION FOR EXTENSION OF TIME — PUBLIC COMPANY
            <span v-if="!isPrivate" class="sec-badge">Active</span>
          </th>
        </tr>
        <template v-if="!isPrivate">
          <tr>
            <td class="cdd-lcell" style="width:58%">Date of Financial Year End</td>
            <td><ExtensionOfTimeDocVarField field-key="financial_year_end" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Last date to hold Annual General Meeting</td>
            <td><ExtensionOfTimeDocVarField field-key="agm_deadline" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Proposed period for extension of the Circulation of Financial Statement (Fee: RM 100)</td>
            <td><ExtensionOfTimeDocVarField field-key="proposed_circulation_extension" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Proposed period for extension of the Financial Statement Lodgement (Fee: RM 100)</td>
            <td><ExtensionOfTimeDocVarField field-key="proposed_lodgement_extension" /></td>
          </tr>
          <tr>
            <td class="cdd-lcell">Reasons for applying the extension of time</td>
            <td><ExtensionOfTimeDocVarField field-key="extension_reason" /></td>
          </tr>
        </template>
        <template v-else>
          <tr><td class="cdd-lcell" style="width:58%">Date of Financial Year End</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Last date to hold Annual General Meeting</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Proposed period for extension of the Circulation of Financial Statement (Fee: RM 100)</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Proposed period for extension of the Financial Statement Lodgement (Fee: RM 100)</td><td class="nil">NIL</td></tr>
          <tr><td class="cdd-lcell">Reasons for applying the extension of time</td><td class="nil">NIL</td></tr>
        </template>
      </tbody>
    </table>

    <p class="doc-lbl-title" style="margin-top:18px">Declaration:</p>
    <p class="doc-p">
      I confirm that the facts and information stated in this document are true and to the best of my knowledge.
    </p>

    <p class="doc-p"><b>Signed:</b></p>
    <ExtensionSigners var-key="notice" />
    <p class="doc-p" style="font-weight:600">Director</p>
    <p class="doc-p"><b>Date :</b> <ExtensionOfTimeDocVarField field-key="declaration_date" /></p>

    <div class="doc-attn">
      <b>Attention:</b>
      It is an offence under section 591 of the Companies Act 2016 to make or authorize the making of a statement
      that a person knows is false or misleading and that person may be liable, upon conviction, to imprisonment
      for a term not exceeding ten years or to a fine not exceeding RM3million or to both.
    </div>
  </div>
</template>

<style src="./extensionOfTimeDoc.css"></style>
<style scoped>
.note-line { font-size: 11px; color: #555; font-style: italic; margin: 10px 0 2px; }
.type-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 12px 0 4px; }
.type-chip { font-size: 12px; opacity: .55; }
.type-chip.on { opacity: 1; }
.sec-badge {
  margin-left: 8px; font-size: 10px; font-weight: 800; color: #fff;
  background: #22a06b; border-radius: 999px; padding: 1px 8px; vertical-align: middle;
}
</style>
